import { AgentEvent, LlmMessage } from '~/atoms/schemas/workflowSchemas';
import { ProcessedEvent } from '../types';
import { getEventNameFromMessageType } from './getEventNameFromMessageType';

/**
 * Matches LLM messages with existing events in the event map and replaces or adds them as needed.
 *
 * @param eventMap - Map of processed events, keyed by workflow_id-task_id-index
 * @param llmMessages - Array of LLM messages to reconcile with the event map
 * @param eventsLength - Number of events (used to determine if we're in a non-streaming scenario)
 * @returns - Updated map with reconciled events
 */
export const matchLlmMessagesWithEvents = (
  eventMap: Map<string, ProcessedEvent>,
  llmMessages: LlmMessage[],
  eventsLength: number = 0,
): Map<string, ProcessedEvent> => {
  // Create a new map to avoid mutating the input map
  const updatedEventMap = new Map(eventMap);

  // Process each LLM message
  llmMessages.forEach((message) => {
    // Only process messages that have an index
    if (message.index === undefined) return;

    // Create the key using the same structure as in processAgentEvents
    const eventKey = `${message.workflow_id}-${message.task_id}-${message.index}`;
    const matchingEvent = updatedEventMap.get(eventKey);

    // Check if we have a matching event with the same type
    if (matchingEvent && matchingEvent.messageType === message.type) {
      // Mark the matching event as replaced
      updatedEventMap.set(eventKey, {
        ...matchingEvent,
        replacedByLlmMessage: true,
      });

      // Create a finalized event key that includes workflow_id, task_id, index and type
      const finalizedKey = `${message.workflow_id}-${message.task_id}-${message.index}-${message.type}-finalized`;

      // Determine event type based on the message type
      const eventName = getEventNameFromMessageType(message.type);

      // Create or update the finalized version
      updatedEventMap.set(finalizedKey, {
        uuid_run: message.run_id,
        index: message.index,
        eventName,
        content: message.content,
        timestamp: message.timestamp,
        isComplete: true,
        workflow_id: message.workflow_id,
        task_id: message.task_id,
        messageType: message.type,
      });
    } else {
      // If no matching streaming event exists, just add the finalized message directly
      // Only do this for non-streaming scenarios (page refresh)
      if (eventsLength <= 1) {
        // Allow for single heartbeat event
        const finalizedKey = `${message.workflow_id}-${message.task_id}-${message.index}-${message.type}-finalized`;

        const eventName = getEventNameFromMessageType(message.type);

        updatedEventMap.set(finalizedKey, {
          uuid_run: `${message.run_id}-finalized`,
          index: message.index,
          eventName,
          content: message.content,
          timestamp: message.timestamp,
          isComplete: true,
          workflow_id: message.workflow_id,
          task_id: message.task_id,
          messageType: message.type,
        });
      }
    }
  });

  return updatedEventMap;
};
